import { z } from 'zod'
import type { PhotoFrameEntryData } from '@/schemas/v2/photo-frame'
import { DateTimeStringSchema } from './common'

// ── フォトフレーム関連を photo-frame.ts から re-export（後方互換）──────────
export {
  PhotoFrameItemDataSchema as SpotPhotoFrameItemDataSchema,
  PhotoFrameEntryDataSchema as SpotPhotoFrameEntryDataSchema,
  PhotoFrameResponseSchema as SpotPhotoFrameResponseSchema,
  PhotoFrameItemSchema,
  toPhotoFrameItems,
} from '@/schemas/v2/photo-frame'
export type {
  PhotoFrameItemData as SpotPhotoFrameItemData,
  PhotoFrameEntryData as SpotPhotoFrameEntryData,
  PhotoFrameResponse as SpotPhotoFrameResponse,
  PhotoFrameItem,
} from '@/schemas/v2/photo-frame'

// ─── クーポン（SpotDetail 表示用 ・ 互換型） ─────────────────────────
export const SpotCouponDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  image: z.string().nullable(),
  code: z.string().nullable(),
  description: z.string().nullable(),
  start: DateTimeStringSchema.nullable(),
  end: DateTimeStringSchema.nullable(),
  message: z.string().nullable(),
  note: z.string().nullable(),
  quantity: z.number().nullable(),
})
export type SpotCouponData = z.infer<typeof SpotCouponDataSchema>

// ─── サブエンドポイント用スキーマ ─────────────────────────────────────

/** POST /api/v2/spots/{id}/audio-guides レスポンス内 エントリ */
export const SpotAudioGuideEntryDataSchema = z.object({
  id: z.number(),
  caption: z.string().nullable(),
  file: z.string().nullable(),
  message: z.string().nullable(),
  status: z.enum(['available', 'out_of_range']),
})
export type SpotAudioGuideEntryData = z.infer<typeof SpotAudioGuideEntryDataSchema>

/** POST /api/v2/spots/{id}/audio-guides レスポンス */
export const SpotAudioGuideResponseSchema = z.object({
  audioGuides: z.array(SpotAudioGuideEntryDataSchema),
})
export type SpotAudioGuideResponse = z.infer<typeof SpotAudioGuideResponseSchema>

/** GET /api/v2/spots/{id}/coupons レスポンス内 エントリ */
export const SpotCouponEntryDataSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  description: z.string().nullable(),
  startAt: DateTimeStringSchema.nullable(),
  endAt: DateTimeStringSchema.nullable(),
  status: z.enum([
    'available',
    'stamp_required',
    'redeemed',
    'period_out',
    'out_of_range',
    'expired',
  ]),
  message: z.string().nullable(),
  usageLimitType: z.enum(['ONCE', 'DAILY', 'UNLIMITED']),
  validDays: z.number().nullable(),
})
export type SpotCouponEntryData = z.infer<typeof SpotCouponEntryDataSchema>

/** GET /api/v2/spots/{id}/coupons レスポンス */
export const SpotCouponsResponseSchema = z.object({
  coupons: z.array(SpotCouponEntryDataSchema),
})
export type SpotCouponsResponse = z.infer<typeof SpotCouponsResponseSchema>

/** POST /api/v2/spots/{spotId}/coupons/{couponId} レスポンス内クーポンデータ */
export const SpotCouponRedeemCouponDataSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  image: z.string().nullable(),
})
export type SpotCouponRedeemCouponData = z.infer<typeof SpotCouponRedeemCouponDataSchema>

/** POST /api/v2/spots/{spotId}/coupons/{couponId} レスポンス内スポットデータ */
export const SpotCouponRedeemSpotDataSchema = z.object({
  id: z.number(),
  name: z.string(),
})
export type SpotCouponRedeemSpotData = z.infer<typeof SpotCouponRedeemSpotDataSchema>

/** POST /api/v2/spots/{spotId}/coupons/{couponId} レスポンス */
export const SpotCouponRedeemResponseSchema = z.object({
  id: z.number(),
  status: z.literal('success'),
  message: z.string(),
  usedAt: DateTimeStringSchema,
  coupon: SpotCouponRedeemCouponDataSchema,
  spot: SpotCouponRedeemSpotDataSchema,
})
export type SpotCouponRedeemResponse = z.infer<typeof SpotCouponRedeemResponseSchema>

/**
 * POST /photo-frames レスポンスからロック状態を判定する。
 * エントリが存在し、全て stamp_required / out_of_range → locked
 */
export const isPhotoFrameLocked = (entries: PhotoFrameEntryData[]): boolean =>
  entries.length > 0 && entries.every((e) => e.status !== 'available')

/**
 * POST /audio-guides レスポンスを DialogSpotsAudioGuide 用アイテムに変換する。
 * status が 'available' のものだけを含める。
 */
export const toAudioGuideDialogItems = (
  entries: SpotAudioGuideEntryData[],
): { caption: string | null; audio: string | null }[] =>
  entries.filter((e) => e.status === 'available').map((e) => ({ caption: e.caption, audio: e.file }))

/**
 * POST /audio-guides レスポンスからロック状態を判定する。
 * エントリが存在し、全て out_of_range → locked
 */
export const isAudioGuideLocked = (entries: SpotAudioGuideEntryData[]): boolean =>
  entries.length > 0 && entries.every((e) => e.status === 'out_of_range')

// ─── スポット公式SNS ─────────────────────────────────────────────────
/** http/https スキームのみ許可するURLチェック */
const isHttpUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url)
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

export const SpotOfficialSnsDataSchema = z.object({
  type: z.enum(['facebook', 'instagram', 'x', 'line', 'tiktok']).describe('SNS種別'),
  url: z.string().describe('公式SNS URL'),
})
export type SpotOfficialSnsData = z.infer<typeof SpotOfficialSnsDataSchema>

// ─── スポットタグ ─────────────────────────────────────────────────────
const SpotTagDataSchema = z.object({
  id: z.number(),
  name: z.string(),
})
export type SpotTagData = z.infer<typeof SpotTagDataSchema>

// ─── スタンプ（GET /api/v2/spots/{id} の詳細レスポンスに含まれる） ──
export const SpotStampDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.enum(['GPS', 'QR_CODE', 'KEYWORD', 'BARCODE', 'OCR']),
  /** スタンプ押印開始日時（ISO 8601）。null の場合はキャンペーンの押印期間に従う */
  startAt: DateTimeStringSchema.nullable().optional(),
  /** スタンプ押印終了日時（ISO 8601）。null の場合はキャンペーンの押印期間に従う */
  endAt: DateTimeStringSchema.nullable().optional(),
  /**
   * 押印可能開始時間（HH:MM:SS）。
   * open と close の**両方**が null または両方が "00:00:00" の場合のみ全日 OK。
   * それ以外（片方のみ設定など）はバックエンドの isWithinBusinessHours() に従い個別に時間帯制限が適用される。
   */
  open: z.string().regex(/^\d{2}:\d{2}:\d{2}$/).nullable().optional(),
  /**
   * 押印可能終了時間（HH:MM:SS）。
   * open と close の**両方**が null または両方が "00:00:00" の場合のみ全日 OK。
   * それ以外（片方のみ設定など）はバックエンドの isWithinBusinessHours() に従い個別に時間帯制限が適用される。
   */
  close: z.string().regex(/^\d{2}:\d{2}:\d{2}$/).nullable().optional(),
  /** OCR注意喚起設定（type が OCR かつ設定がある場合のみ。それ以外は null） */
  ocrWarning: z
    .object({
      enabled: z.boolean(),
      title: z.string().nullable(),
      /** HTML文字列 */
      content: z.string().nullable(),
    })
    .nullable()
    .optional(),
})
export type SpotStampData = z.infer<typeof SpotStampDataSchema>

// ─── SpotData（GET /api/v2/spots 一覧・共通ベース） ─────────────────
export const SpotDataSchema = z.object({
  id: z.number(),
  campaignId: z.number(),
  name: z.string(),
  address: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  url: z.string().nullable(),
  description: z.string().nullable(),
  images: z.array(z.string()),
  sheets: z.array(z.string()),
  /** Stamp→Sheet リレーション経由の有効シート種別ユニーク一覧 */
  sheetTypes: z.array(z.enum(['stamp_rally', 'point_rally', 'point_card', 'overlay'])).default([]),
  tags: z.array(SpotTagDataSchema).default([]),
  /** 音声ガイドが設定されているか */
  audioGuide: z.boolean().default(false),
  /** クーポンが設定されているか */
  coupon: z.boolean().default(false),
  /** フォトフレームが設定されているか */
  photoFrame: z.boolean().default(false),
  /** 現在押印可能なスタンプが1件以上あるか（キャンペーン押印期間・スタンプ固有期間・営業時間をすべて考慮） */
  hasStamp: z.boolean().default(false),
  /** お気に入り状態（認証済み時のみ設定、未認証時は null） */
  favorite: z.boolean().nullable().optional(),
})

// ─── SpotDetailData（GET /api/v2/spots/{id} 詳細レスポンス内） ──────
export const SpotDetailDataSchema = SpotDataSchema.extend({
  /** スポットまでの距離（km）。lat/lon 未指定時は null */
  distanceKm: z.number().nullable(),
  /** GPS 範囲内判定（サーバーサイド計算）。GPS判定スタンプ0件または lat/lon 未指定時は null */
  isInRange: z.boolean().nullable(),
  /** スポットに紐づくスタンプ一覧（削除済みを除く） */
  stamps: z.array(SpotStampDataSchema).default([]),
  /** スポットの公式SNSリンク一覧（APIレスポンスの順に格納）。SNSが設定されていない場合は空配列。
   * http/https 以外のURLは安全のため自動的に除外される。 */
  officialSns: z
    .array(SpotOfficialSnsDataSchema)
    .default([])
    .transform((items) => items.filter((item) => isHttpUrl(item.url))),
})
export type SpotDetailData = z.infer<typeof SpotDetailDataSchema>

export const SpotGetResponseSchema = z.object({
  spot: SpotDetailDataSchema.nullable(),
})

export const SpotListResponseSchema = z.object({
  spots: z.array(SpotDataSchema),
})

export type SpotData = z.infer<typeof SpotDataSchema>
export type SpotGetResponse = z.infer<typeof SpotGetResponseSchema>
export type SpotListResponse = z.infer<typeof SpotListResponseSchema>
